import React, { useEffect, useState } from "react";
import {
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Box,
  Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { DataGrid } from "@mui/x-data-grid";
// import RenderMessageWithReferences from '../RenderMessageWithReferences';

import ReactMarkdown from "react-markdown";
import InfoIcon from "@mui/icons-material/Info";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";

const Top10QnAList = ({ chatDetails }) => {
  const [sessionRows, setSessionRows] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);

  useEffect(() => {
    if (chatDetails?.length) {
      const grouped = chatDetails.reduce((acc, item) => {
        const sessionId = item.session?.id || "unknown";
        if (!acc[sessionId]) {
          acc[sessionId] = {
            id: sessionId,
            session: item.session,
            questions: [],
          };
        }
        acc[sessionId].questions.push({
          question: item.question,
          answer: item.answer || "No answer yet",
          feedback: item.feedback || "-",
          createdAt: item.createdAt,
          completeResponse: item?.completeResponse,
        });
        return acc;
      }, {});

      // Add questionCount directly to the rows
      const sessionList = Object.values(grouped).map((session) => ({
        ...session,
        questionCount: session.questions.length,
        userName: session.session?.user?.name || "—",
        userEmail: session.session?.user?.email || "—",
      }));

      setSessionRows(sessionList);
    }
  }, [chatDetails]);

  const handleInfoClick = (row) => {
    setSelectedSession(row);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedSession(null);
  };

  const columns = [
    { field: "id", headerName: "Session ID", width: 320 },
    { field: "userName", headerName: "Name", width: 90 },
    { field: "userEmail", headerName: "Email", width: 150 },
    {
      field: "questionCount",
      headerName: "Questions",
      width: 100,
      type: "number",
    },
    {
      field: "info",
      headerName: "",
      width: 60,
      sortable: false,
      renderCell: (params) => (
        <IconButton onClick={() => handleInfoClick(params.row)} size="small">
          <InfoOutlinedIcon color="primary" />
        </IconButton>
      ),
    },
  ];

  // const handleOpenReference = (documentId, selectedPages) => {
  //   console.log("Opening reference:", {
  //     documentId,
  //     selectedPages,
  //     drawerState: drawerState,
  //   });
  //   setDrawerState({
  //     visible: true,
  //     documentId,
  //     selectedPages,
  //   });
  // };

  // const handleCloseDrawer = () => {
  //   console.log("Closing drawer");
  //   setDrawerState({
  //     visible: false,
  //     documentId: null,
  //     selectedPages: [],
  //   });
  // };

  // const [drawerState, setDrawerState] = useState({
  //   visible: false,
  //   documentId: null,
  //   selectedPages: [],
  // });

  const renderMessageWithReferences = (
    text,
    references = [],
    handleOpenReference
  ) => {
    if (!text) return null;

    const replaceReferences = (children) => {
      return React.Children.map(children, (child) => {
        if (typeof child === "string") {
          // Support both ##0$$ and [ID:0] markers
          const parts = child.split(/(##\d+\$\$|\[ID:\d+\])/g);
          return parts.map((part, index) => {
            const match = part.match(/##(\d+)\$\$|\[ID:(\d+)\]/);
            if (match) {
              const refIndex = parseInt(match[1] || match[2], 10);
              const ref = references[refIndex];
              if (!ref) return null;

              const seenPages = new Set();
              const uniquePages = Array.isArray(ref.positions)
                ? ref.positions
                    .filter((p) => Array.isArray(p) && p.length > 0 && p[0])
                    .map((p) => p[0])
                    .filter((page) => {
                      if (seenPages.has(page)) return false;
                      seenPages.add(page);
                      return true;
                    })
                : [];

              return (
                <IconButton
                  key={`ref-btn-${index}`}
                  onClick={() =>
                    handleOpenReference?.(ref.document_id, uniquePages)
                  }
                  size="small"
                  sx={{
                    padding: "2px",
                    margin: "0 2px",
                    verticalAlign: "middle",
                  }}
                >
                  <InfoIcon sx={{ fontSize: 16, color: "#007BFF" }} />
                </IconButton>
              );
            }
            return part;
          });
        } else if (React.isValidElement(child)) {
          return React.cloneElement(child, {
            children: replaceReferences(child.props.children),
          });
        }
        return child;
      });
    };

    return (
      <ReactMarkdown
        components={{
          p: ({ node, children, ...props }) => (
            <div style={{ marginBottom: "16px", lineHeight: "1.6" }} {...props}>
              {replaceReferences(children)}
            </div>
          ),
          li: ({ node, children, ...props }) => (
            <li style={{ marginBottom: "8px", lineHeight: "1.6" }} {...props}>
              {replaceReferences(children)}
            </li>
          ),
          ul: ({ node, ...props }) => (
            <ul
              style={{
                paddingLeft: "24px",
                marginTop: "12px",
                marginBottom: "12px",
              }}
              {...props}
            />
          ),
          ol: ({ node, ...props }) => (
            <ol
              style={{
                paddingLeft: "24px",
                marginTop: "12px",
                marginBottom: "12px",
              }}
              {...props}
            />
          ),
          strong: ({ node, ...props }) => (
            <strong style={{ fontWeight: 600, color: "#000" }} {...props} />
          ),
        }}
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
      >
        {text}
      </ReactMarkdown>
    );
  };

  // const renderMessageWithReferencesOld = (text, references) => {
  //   if (!text) return null;

  //   const replaceReferences = (children) => {
  //     return React.Children.map(children, (child) => {
  //       if (typeof child === "string") {
  //         const parts = child.split(/(##\d+\$\$)/g);
  //         return parts.map((part, index) => {
  //           const match = part.match(/##(\d+)\$\$/);
  //           if (match) {
  //             const refIndex = parseInt(match[1], 10);
  //             const ref = references && references[refIndex];
  //             if (!ref) return null;

  //             console.log("Reference data:", {
  //               refIndex,
  //               ref,
  //               documentId: ref.document_id,
  //               positions: ref.positions,
  //             });

  //             // Extract and deduplicate page numbers from positions array
  //             const seenPages = new Set();
  //             const uniquePages = ref.positions
  //               .filter((p) => Array.isArray(p) && p.length > 0 && p[0])
  //               .map((p) => p[0])
  //               .filter((page) => {
  //                 if (seenPages.has(page)) return false;
  //                 seenPages.add(page);
  //                 return true;
  //               });

  //             return (
  //               <IconButton
  //                 key={`ref-btn-${index}`}
  //                 onClick={() =>
  //                   handleOpenReference(ref.document_id, uniquePages)
  //                 }
  //                 size="small"
  //                 sx={{
  //                   padding: "2px",
  //                   margin: "0 2px",
  //                   verticalAlign: "middle",
  //                 }}
  //               >
  //                 <InfoIcon sx={{ fontSize: 16, color: "#007BFF" }} />
  //               </IconButton>
  //             );
  //           }
  //           return part;
  //         });
  //       } else if (React.isValidElement(child)) {
  //         return React.cloneElement(child, {
  //           children: replaceReferences(child.props.children),
  //         });
  //       }
  //       return child;
  //     });
  //   };

  //   return (
  //     <ReactMarkdown
  //       components={{
  //         p: ({ node, children, ...props }) => (
  //           <div style={{ marginBottom: "16px", lineHeight: "1.6" }} {...props}>
  //             {replaceReferences(children)}
  //           </div>
  //         ),
  //         li: ({ node, children, ...props }) => (
  //           <li style={{ marginBottom: "8px", lineHeight: "1.6" }} {...props}>
  //             {replaceReferences(children)}
  //           </li>
  //         ),
  //         ul: ({ node, ...props }) => (
  //           <ul
  //             style={{
  //               paddingLeft: "24px",
  //               marginTop: "12px",
  //               marginBottom: "12px",
  //             }}
  //             {...props}
  //           />
  //         ),
  //         ol: ({ node, ...props }) => (
  //           <ol
  //             style={{
  //               paddingLeft: "24px",
  //               marginTop: "12px",
  //               marginBottom: "12px",
  //             }}
  //             {...props}
  //           />
  //         ),
  //         strong: ({ node, ...props }) => (
  //           <strong style={{ fontWeight: 600, color: "#000" }} {...props} />
  //         ),
  //       }}
  //       remarkPlugins={[remarkGfm]}
  //       rehypePlugins={[rehypeRaw]}
  //     >
  //       {text}
  //     </ReactMarkdown>
  //   );
  // };

  return (
    <>
      {console.log("selectedSession---->", selectedSession)}
      <Paper elevation={3} sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          🧠 Top Sessions
        </Typography>
        <DataGrid
          rows={sessionRows}
          columns={columns}
          autoHeight
          density="compact"
          disableColumnMenu
          disableRowSelectionOnClick
          pageSizeOptions={[10]}
          onRowClick={(params) => handleInfoClick(params.row)}
          sx={{
            "& .MuiDataGrid-row": {
              cursor: "pointer",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            },
          }}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10, page: 0 },
            },
          }}
        />
      </Paper>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            m: { xs: 1, sm: 2 },
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{ display: "flex", justifyContent: "space-between", pr: 2 }}
        >
          <Typography variant="h6" component="span">
            Session Detail
          </Typography>
          <IconButton onClick={handleCloseDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ bgcolor: "#f9f9f9" }}>
          {selectedSession && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {/* Session Info */}
              <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>
                  📁 Session Info
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography>
                      <strong>Session ID:</strong> {selectedSession.session?.id}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography>
                      <strong>Created:</strong>{" "}
                      {new Date(
                        selectedSession.session?.createdAt
                      ).toLocaleString()}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* User Info */}
              {selectedSession.session?.user && (
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    👤 User Info
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography>
                        <strong>Name:</strong>{" "}
                        {selectedSession.session.user.name}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography>
                        <strong>Email:</strong>{" "}
                        {selectedSession.session.user.email}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography>
                        <strong>KB Agent Id:</strong>{" "}
                        {selectedSession.session.user.kbId}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography>
                        <strong>Joined:</strong>{" "}
                        {new Date(
                          selectedSession.session.user.createdAt
                        ).toLocaleString()}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              )}

              {/* All QnA */}
              <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>
                  💬 All Converstion
                </Typography>
                {selectedSession.questions.map((qna, idx) => (
                  <Box
                    key={idx}
                    sx={{ mb: 2, p: 1, borderBottom: "1px solid #ddd" }}
                  >
                    <Typography>
                      <strong>Q{idx + 1} :</strong> {qna.question}
                    </Typography>
                    <Typography>
                      <strong>A :</strong>{" "}
                      {qna?.completeResponse != null
                        ? renderMessageWithReferences(
                            qna.completeResponse.message
                          )
                        : qna.answer}
                    </Typography>
                    <Typography>
                      <strong>Feedback:</strong>{" "}
                      {qna.feedback === "thumbs_up"
                        ? "👍"
                        : qna.feedback === "thumbs_down"
                        ? "👎"
                        : "—"}
                    </Typography>
                    <Typography variant="caption">
                      <strong>Asked At:</strong>{" "}
                      {new Date(qna.createdAt).toLocaleString()}
                    </Typography>
                  </Box>
                ))}
              </Paper>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleCloseDialog}
            variant="contained"
            color="primary"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Top10QnAList;
